// The GDPR-routes-to-founder banner + confirmation panel (docs/design/
// slice4-certify-ux.md §4.4; D46/D47 hard-gate precedent - a personal-data action
// always waits for the founder's one-tap). The banner is `role="alert"` only on first
// appearance (it mounts once real routing data arrives and stays mounted - no polling
// exists this slice, so it is never re-announced); the panel is a real focus trap
// (`escape-routes`/`focus-management`): focus moves in on open, Tab/Shift+Tab cycle
// within it, Escape is the explicit "Not now" exit, and focus returns to the trigger
// on close.
import { useEffect, useId, useRef } from "react";
import { certifyCopy } from "../copy/certify.js";

export interface RoutingItem {
  readonly controlKey: string;
  readonly statement: string;
}

export const GdprRoutingBanner = ({
  count,
  canAct,
  onOpen,
}: {
  readonly count: number;
  readonly canAct: boolean;
  readonly onOpen: () => void;
}): React.JSX.Element | null => {
  if (count === 0) return null;
  return (
    <div className="banner top amber" role="alert" data-testid="gdpr-routing-banner">
      <span className="dot warn" aria-hidden="true" />
      <div className="grow">{certifyCopy("certify.dashboard.gdprRouting.bannerCount", { count })}</div>
      {canAct ? (
        <button type="button" className="btn btn-dark btn-sm" onClick={onOpen}>
          {certifyCopy("certify.dashboard.gdprRouting.openCta")}
        </button>
      ) : null}
    </div>
  );
};

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const GdprRoutingPanel = ({
  items,
  onConfirm,
  onClose,
  triggerRef,
}: {
  readonly items: readonly RoutingItem[];
  readonly onConfirm: (controlKey: string) => void;
  readonly onClose: () => void;
  /** The control that opened the panel - focus returns here on close
   * (`focus-management`). Optional: a caller with nothing to restore focus to (e.g. a
   * standalone test) simply gets no restore. */
  readonly triggerRef?: React.RefObject<HTMLElement | null>;
}): React.JSX.Element => {
  const panelRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const headingId = useId();

  // Runs once on mount (focus the heading) and once on unmount (restore focus to the
  // trigger) - `triggerRef` is a ref object (stable identity), so it is intentionally
  // omitted from the dependency array rather than re-running this effect.
  useEffect(() => {
    headingRef.current?.focus();
    return () => {
      triggerRef?.current?.focus();
    };
  }, [triggerRef]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== "Tab" || panelRef.current === null) return;
    const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="gdpr-routing-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      ref={panelRef}
      onKeyDown={handleKeyDown}
      data-testid="gdpr-routing-panel"
    >
      <div className="card pad">
        <h2 className="h3" id={headingId} tabIndex={-1} ref={headingRef}>
          Items that need your decision
        </h2>
        <p className="muted mt8" style={{ fontSize: 13 }}>
          {certifyCopy("certify.dashboard.gdprRouting.panelWhy")}
        </p>
        <ul className="stack gap12 mt12" style={{ margin: 0, padding: 0, listStyle: "none" }}>
          {items.map((item) => (
            <li key={item.controlKey} className="well pad">
              <p style={{ margin: 0, fontSize: 13.5 }}>{item.statement}</p>
              <button
                type="button"
                className="btn btn-primary btn-sm mt8"
                onClick={() => {
                  onConfirm(item.controlKey);
                }}
              >
                {certifyCopy("certify.dashboard.gdprRouting.confirmCta")}
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="btn btn-ghost mt16" onClick={onClose}>
          {certifyCopy("certify.dashboard.gdprRouting.notNowCta")}
        </button>
      </div>
    </div>
  );
};
